import { type Draft, produce } from 'immer';

import { type EngineStateBase } from '@blaze-runtime/engine-data-access';
import {
  type CanvasElementFrom,
  type CanvasElementId,
  type ColumnId,
  type ColumnResizeData,
  type SlotData,
  type SlotId,
  type SlotIdOf,
  type Slots,
  type SlotSizing,
  type SlotSizingMode,
} from '@blaze-runtime/engine-domain';

import { hasSlots } from '../../canvas-elements/element-model/element-props-guards';
import { isElementType } from '../../canvas-elements/element-model/element-type-guards';

export function setColumnWidthPercentDrag(
  state: EngineStateBase,
  elementId: CanvasElementId,
  slotId: ColumnId,
  newWidth: number,
): EngineStateBase {
  return produce(state, (draft) => {
    const element = draft.entities[elementId];

    if (!element) {
      throw new Error(`Could not find element id ${elementId}`);
    }

    if (!hasSlots(element)) {
      throw new Error(`Element id ${elementId} is missing slots`);
    }

    const slot = element.slots[slotId];
    if (!slot) {
      throw new Error('Could not find slot');
    }

    const currentSlotIndex = element.slots.ids.findIndex((id) => id === slotId);
    const nextSlotId = element.slots.ids[currentSlotIndex + 1];
    const nextSlot = element.slots[nextSlotId];
    const hasNextSlot = nextSlot !== undefined;

    if (newWidth === 0) {
      slot.sizing = { mode: 'fit-content' };
      if (hasNextSlot) {
        nextSlot.sizing = { mode: 'auto' };
      }
    } else if (newWidth === 100) {
      slot.sizing = { mode: 'auto' };
      // Set all next slots to 'fit-content'
      setFollowingColumnsToFitContent(element.slots, currentSlotIndex);
    } else {
      if (hasNextSlot) {
        nextSlot.sizing = getPercentResizeNextSlotSizing(newWidth, slot.sizing, nextSlot.sizing);
      }

      slot.sizing = { mode: 'percent', width: newWidth };
    }
  });
}

/**
 * Sets all columns following `startIndex` (not inclusive) to 'fit-content' mode.
 * This function mutates the input data, so it needs to be used in immerjs `produce` context.
 */
function setFollowingColumnsToFitContent(slots: Slots<SlotId>, startIndex: number): void {
  for (let i = startIndex + 1; i < slots.ids.length; i++) {
    const slot = slots[slots.ids[i]];
    if (!slot) {
      throw new Error('Could not find slot');
    }

    slot.sizing = { mode: 'fit-content' };
  }
}

function getPercentResizeNextSlotSizing(
  newWidth: number,
  sizing?: SlotSizing,
  nextSizing?: SlotSizing,
): SlotSizing {
  // If next slot is percent sized, reduce its size by the difference in width of current slot
  if (
    nextSizing?.mode === 'percent' &&
    nextSizing?.width !== undefined &&
    sizing?.mode === 'percent' &&
    sizing?.width !== undefined
  ) {
    const widthDelta = newWidth - sizing.width;

    return {
      ...nextSizing,
      width: (nextSizing?.width || 0) - widthDelta,
    };
    // Otherwise - set the next slot to 'auto'
  } else {
    return { mode: 'auto' };
  }
}

export function setDesiredColumnWidthDirect(
  state: EngineStateBase,
  elementId: CanvasElementId,
  slotId: ColumnId,
  resizeData: ColumnResizeData[],
  newWidthPercent: number,
): EngineStateBase {
  return produce(state, (draft) => {
    const element = draft.entities[elementId || ''];

    if (
      !isElementType(element, 'container') &&
      !isElementType(element, 'navHeaderContainer') &&
      !isElementType(element, 'navBottombarContainer')
    ) {
      throw new Error('Only container elements can have their columns resized!');
    }

    const targetSlot = element.slots[slotId];
    if (!targetSlot) {
      throw new Error(`Could not find slot to process. SlotId: ${slotId}`);
    }

    const targetSlotIdIndex = resizeData.findIndex((crd) => crd.slotId === slotId);
    const targetSlotResizeData = resizeData[targetSlotIdIndex];
    const { widthPercent, maxWidthPercent } = targetSlotResizeData;

    // Determine how much space to get
    let delta = newWidthPercent - widthPercent;

    // Cap size at maximum possible
    if (newWidthPercent > maxWidthPercent) {
      delta = maxWidthPercent - widthPercent;
    }

    if (widthPercent + delta >= maxWidthPercent) {
      delta = maxWidthPercent - widthPercent;
    }

    if (resizeData.length === 1) {
      targetSlot.sizing = { mode: 'auto' };
      return;
    }

    if (delta <= 0) {
      shrinkColumnMutably(element, resizeData, targetSlotIdIndex, targetSlot, Math.abs(delta));
    } else {
      expandColumnMutably(element, resizeData, targetSlotIdIndex, targetSlot, delta);
    }
  });
}

function shrinkColumnMutably(
  element: Draft<
    | CanvasElementFrom<'container'>
    | CanvasElementFrom<'navHeaderContainer'>
    | CanvasElementFrom<'navBottombarContainer'>
  >,
  resizeData: ColumnResizeData[],
  targetSlotIdIndex: number,
  targetSlot: Draft<SlotData>,
  shrinkBy: number,
): void {
  const targetSlotResizeData = resizeData[targetSlotIdIndex];
  const slotsCount = resizeData.length;

  const nextSlotId = resizeData[targetSlotIdIndex + 1]?.slotId;
  const nextSlot = element.slots[nextSlotId];
  const nextSlotResizeData = resizeData[targetSlotIdIndex + 1];

  const prevSlotId = resizeData[targetSlotIdIndex - 1]?.slotId;
  const prevSlot = element.slots[prevSlotId];
  const prevSlotResizeData = resizeData[targetSlotIdIndex - 1];

  const calculatedWidth = targetSlotResizeData.widthPercent - shrinkBy;
  const desiredWidth = calculatedWidth > targetSlotResizeData.minWidthPercent ? calculatedWidth : 0;

  const isNeighbourAutoWidthColumnPresent = resizeData.some((crd, index) => {
    if (index === targetSlotIdIndex) {
      return false;
    }
    return element.slots[crd.slotId]?.sizing?.mode === 'auto';
  });

  // If any neighbour column has width auto there is no need to shrink them. Just shrink target column.
  if (isNeighbourAutoWidthColumnPresent) {
    targetSlot.sizing =
      desiredWidth === 0 ? { mode: 'fit-content' } : { mode: 'percent', width: desiredWidth };
    return;
  }

  // Try to shrink column next to target
  if (targetSlotIdIndex < slotsCount - 1 && nextSlot && nextSlotResizeData) {
    if (desiredWidth === 0) {
      targetSlot.sizing = { mode: 'fit-content' };
      nextSlot.sizing = { mode: 'auto' };
    } else {
      targetSlot.sizing = { mode: 'percent', width: desiredWidth };
      nextSlot.sizing = { mode: 'percent', width: nextSlotResizeData.widthPercent + shrinkBy };
    }
    return;
  }

  if (targetSlotIdIndex >= slotsCount - 1 && prevSlot && prevSlotResizeData) {
    if (desiredWidth === 0) {
      targetSlot.sizing = { mode: 'fit-content' };
    } else {
      targetSlot.sizing = { mode: 'percent', width: desiredWidth };
    }
    prevSlot.sizing = { mode: 'auto' };
  }
}

function expandColumnMutably(
  element: Draft<
    | CanvasElementFrom<'container'>
    | CanvasElementFrom<'navHeaderContainer'>
    | CanvasElementFrom<'navBottombarContainer'>
  >,
  resizeData: ColumnResizeData[],
  targetSlotIdIndex: number,
  targetSlot: Draft<SlotData>,
  expandBy: number,
): void {
  const targetSlotResizeData = resizeData[targetSlotIdIndex];
  const slotsCount = resizeData.length;

  let spaceToGetFromOtherSlots = expandBy;
  let foundNewSpace = 0;
  let processingSlotIdIndex = targetSlotIdIndex;
  let direction = targetSlotIdIndex === slotsCount - 1 ? -1 : 1;
  while (spaceToGetFromOtherSlots > 0) {
    processingSlotIdIndex += direction;

    const processingSlotId = resizeData[processingSlotIdIndex].slotId;
    const processingSlot = element.slots[processingSlotId];
    const processingSlotResizeData = resizeData[processingSlotIdIndex];
    const slotsFreeSpace =
      processingSlotResizeData.widthPercent - processingSlotResizeData.minWidthPercent;
    const spaceToTake =
      spaceToGetFromOtherSlots > slotsFreeSpace
        ? slotsFreeSpace // If needed space is bigger than slot can provide take all of it
        : spaceToGetFromOtherSlots; // Otherwise tak needed space

    if (!processingSlot) {
      throw new Error(`Could not find slot to process. SlotId: ${processingSlotId}`);
    }

    if (processingSlotIdIndex < slotsCount - 1) {
      const newWidth = Math.max(
        processingSlotResizeData.widthPercent - spaceToTake,
        processingSlotResizeData.minWidthPercent,
      );
      processingSlot.sizing = { mode: 'percent', width: newWidth };
    } else {
      // For last column we desire it to be auto
      processingSlot.sizing = { mode: 'auto' };
    }

    spaceToGetFromOtherSlots -= spaceToTake;
    foundNewSpace += spaceToTake;

    // Default space is taken from columns from left to right
    // If reached last column walk opposite direction
    if (processingSlotIdIndex === resizeData.length - 1) {
      direction = -1;
      processingSlotIdIndex = targetSlotIdIndex;
    }

    // End algorithm on first column
    if (processingSlotIdIndex === 0) {
      spaceToGetFromOtherSlots = 0;
    }
  }

  if (targetSlotIdIndex < slotsCount - 1) {
    targetSlot.sizing = {
      mode: 'percent',
      width: targetSlotResizeData.widthPercent + foundNewSpace,
    };
  } else {
    targetSlot.sizing = { mode: 'auto' };
  }
}

export function equalizeContainerColumnsWidth(
  state: EngineStateBase,
  elementId: CanvasElementId,
): EngineStateBase {
  return produce(state, (draft) => {
    const element = draft.entities[elementId];

    if (
      !isElementType(element, 'container') &&
      !isElementType(element, 'navHeaderContainer') &&
      !isElementType(element, 'navBottombarContainer')
    ) {
      throw new Error('Only container columns can be equalized');
    }

    const count = element.slots.ids.length;
    const columnWidth = Math.round((100 / count + Number.EPSILON) * 100) / 100;

    element.slots.ids.forEach((id, index) => {
      const slot = element.slots[id];

      if (!slot) {
        return;
      }

      if (index < count - 1) {
        slot.sizing = { mode: 'percent', width: columnWidth };
      } else {
        slot.sizing = { mode: 'auto' };
      }
    });
  });
}

export function setDesiredColumnSizingMode(
  state: EngineStateBase,
  elementId: CanvasElementId,
  slotId: SlotIdOf<'container'>,
  desiredMode: SlotSizingMode,
  resizeData: ColumnResizeData[],
): EngineStateBase {
  return produce(state, (draft) => {
    const element = draft.entities[elementId];

    if (
      !isElementType(element, 'container') &&
      !isElementType(element, 'navHeaderContainer') &&
      !isElementType(element, 'navBottombarContainer')
    ) {
      throw new Error('Only container columns are supported');
    }

    const slotsCount = resizeData.length;
    const slotIdIndex = resizeData.findIndex((rd) => rd.slotId === slotId);
    const slotData = element.slots[slotId];
    const slotResizeData = resizeData[slotIdIndex];

    if (!slotData) {
      return;
    }

    if (slotsCount === 1) {
      slotData.sizing = { mode: 'auto' };
      return;
    }

    const prevSlotId = resizeData[slotIdIndex - 1]?.slotId;
    const prevSlot = element.slots[prevSlotId];
    const nextSlotId = resizeData[slotIdIndex + 1]?.slotId;
    const nextSlot = element.slots[nextSlotId];

    const isLastSlot = slotIdIndex === slotsCount - 1;
    if (desiredMode === 'fit-content') {
      slotData.sizing = { mode: 'fit-content' };

      if (isLastSlot && prevSlot) {
        prevSlot.sizing = { mode: 'auto' };
      } else if (nextSlot) {
        nextSlot.sizing = { mode: 'auto' };
      }

      return;
    }

    if (desiredMode === 'percent') {
      slotData.sizing = { mode: 'percent', width: slotResizeData.widthPercent };
      return;
    }

    slotData.sizing = { mode: 'auto' };
  });
}
